import  { useState } from 'react';
import {
  FormControl,
  InputAdornment,
  TextField,
  styled,
  IconButton,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { messageConstant } from '../../constants/messageConstant';

const CustomTextFieldStyle = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'var(--inputs)',
    color: 'var(--black)',
    fontSize: '0.9rem',
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minHeight: '40px',
    '& .MuiInputBase-input': {
      paddingTop: '15.5px',
      paddingBottom: '15.5px',
      fontFamily: 'monospace',
    },
    '& .MuiInputBase-input:disabled': {
      color: 'var(--black)',
      WebkitTextFillColor: 'var(--black) !important',
      cursor: 'no-drop',
    },
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputLabel-root': {
    position: 'static',
    transform: 'none',
    fontSize: '0.75rem',
    color: 'var(--secondary-text)',
    marginBottom: theme.spacing(0.5),
  },
}));

const maskValue = (value, maskType = 'partial', visibleCharCount = 4) => {
  if (!value) return '';
  if (maskType === 'full') return '*'.repeat(value.length);

  const length = value.length;
  const visibleCount = Math.min(visibleCharCount, length);
  const visiblePart = value.slice(-visibleCount);
  const maskedPart = '*'.repeat(length - visibleCount);

  return maskedPart + visiblePart;
};

const CustomMaskedTextField = ({ properties, methods }) => {
  const {
    inputRef,
    name,
    label = '',
    placeholder,
    isPasswordText = false,
    required,
    regex,
    maxLength,
    isDisabled,
    readOnly,
    validation,
    inputAdornment,
    endInputAdornment,
    maskOnTyping = false,
    visibleCharCount = 4,
    maskType = 'partial',
    showToggleIcon = false,
    capitalize = false,
    customOnBlur,
    onCustomChange,
  } = properties;

  const { control } = methods;
  const [showRealValue, setShowRealValue] = useState(false);

  const toggleVisibility = () => setShowRealValue((prev) => !prev);

  const formatLabelSentenceCase = (text) => {
    const [firstWord, ...rest] = text.split(" ");
    return [firstWord, ...rest.map((w) => w.toLowerCase())].join(" ");
  };
  
  const rules = {
    ...validation,
    required: required ? { value: true, message: `${formatLabelSentenceCase(label)} is required`} : undefined,
    pattern: regex
      ? {
          value: regex,
          message: messageConstant.EnterValid(label),
        }
      : undefined,
    maxLength: maxLength
      ? {
          value: maxLength,
          message: messageConstant.MaxCharacterLength(label, maxLength),
        }
      : undefined,
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
        <FormControl fullWidth>
          <CustomTextFieldStyle
            ref={inputRef}
            inputRef={ref}
            label={label}
            id={name}
            variant="outlined"
            placeholder={placeholder}
            disabled={isDisabled}
            type="text"
            required={required}
            value={
              showRealValue
                ? value
                : maskOnTyping || document.activeElement?.id !== name
                ? maskValue(value, maskType, visibleCharCount)
                : value
            }
            error={!!error}
            helperText={error?.message}
            onBlur={(e) => {
              onBlur();
              const trimmed = e.target.value.trim();
              customOnBlur && customOnBlur(trimmed);
            }}
            onChange={(e) => {
              let rawValue = e.target.value.replace(/\*/g, '');

              if (capitalize) {
                rawValue = rawValue.toUpperCase();
              }

              if (maxLength && rawValue.length > maxLength) {
                rawValue = rawValue.slice(0, maxLength);
              }

              onChange(rawValue);
              onCustomChange && onCustomChange(rawValue);
            }}
            InputProps={{
              readOnly: readOnly,
              startAdornment: inputAdornment ? (
                <InputAdornment position="start">{inputAdornment}</InputAdornment>
              ) : null,
              endAdornment: (
                <>
                  {endInputAdornment && (
                    <InputAdornment position="end">{endInputAdornment}</InputAdornment>
                  )}
                  {showToggleIcon && (
                    <InputAdornment position="end">
                      <IconButton onClick={toggleVisibility} edge="end" tabIndex={-1}>
                        {showRealValue ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )}
                </>
              ),
            }}
            inputProps={{
              maxLength,
              autoComplete: isPasswordText ? 'new-password' : undefined,
            }}
          />
        </FormControl>
      )}
    />
  );
};

export default CustomMaskedTextField;